/**
 * The marketing layer around the demo: what it says, what it links to, and when
 * the welcome video appears.
 *
 * Everything an editor might want to change lives here rather than inside a
 * screen, because these are the parts that change without the app changing: a
 * video that does not exist yet, a form that may be replaced, a window that may
 * turn out to be too long. The app reads them; nothing else defines them.
 */

/**
 * The YouTube id of the walkthrough, or an empty string while there is none.
 *
 * Empty is a supported state, not a placeholder to be tidied up later: with no
 * id the "How to use it?" page says the video is coming and the welcome overlay
 * does not appear at all. An overlay opening onto an empty black rectangle would
 * be worse than no overlay, and this demo is recorded before the video exists.
 * Paste the id - the part after `v=` - and both turn on.
 */
export const VIDEO_ID = "B20w5nP5uVw";

/** Where the welcome overlay and the How-to page point once a video exists. */
export function videoUrl(id: string, autoplay = false): string {
  const parameters = new URLSearchParams({ rel: "0", modestbranding: "1" });
  if (autoplay) parameters.set("autoplay", "1");
  return `https://www.youtube-nocookie.com/embed/${id}?${parameters}`;
}

/**
 * The HubSpot forms the demo embeds.
 *
 * One account, one embed script, two forms: one for a visitor who wants the
 * product, one for a visitor who has an opinion about the demo. Only the form id
 * differs, so it is the only thing stated per form.
 */
export const HUBSPOT = {
  portalId: "6254784",
  region: "na1",
  script: "https://js.hsforms.net/forms/embed/6254784.js",
  forms: {
    contact: "a90c6e69-27b8-41cb-8cfa-3599560bc644",
    feedback: "31fac7f7-0812-4dee-aed6-64ed4046a37b",
  },
} as const;

const WELCOME_KEY = "ai4cad.welcome.lastVisit";
const WELCOME_DAYS = 30;

/**
 * Whether the welcome overlay should open now.
 *
 * Thirty days from the *last* visit: a first-time visitor sees it, and so does
 * anyone who has not opened the demo for thirty days. Someone who keeps coming
 * back has already seen it and is never shown it again while they do.
 *
 * Every access is guarded. Storage throws in a private window and can come back
 * empty after a clear, and neither is a reason to fail: the overlay simply shows
 * again, which is the harmless direction to be wrong in.
 */
export function shouldShowWelcome(): boolean {
  if (!VIDEO_ID) return false;
  const last = readLastVisit();
  if (last === null) return true;
  return Date.now() - last > WELCOME_DAYS * 24 * 60 * 60 * 1000;
}

/** Record this visit, restarting the thirty days. */
export function markVisit(): void {
  try {
    window.localStorage.setItem(WELCOME_KEY, String(Date.now()));
  } catch {
    // A viewer who cannot be remembered sees the overlay again next time. That
    // is the whole consequence, and it is not worth interrupting them over.
  }
}

function readLastVisit(): number | null {
  try {
    const stored = window.localStorage.getItem(WELCOME_KEY);
    if (!stored) return null;
    const when = Number(stored);
    // A value that is not a timestamp is treated as no value: guessing at it
    // would pin the overlay either permanently on or permanently off.
    return Number.isFinite(when) && when > 0 ? when : null;
  } catch {
    return null;
  }
}
